import { registerComponent } from "helpers";
import WYATTElement from "wyatt";

registerComponent(
  "blog-category-dropdown",
  class BlogCategoryDropdown extends WYATTElement {
    constructor() {
      super();

      this.select = this.querySelector("[data-blog-category]");
      this.count = this.querySelector("[data-blog-count]");
      this.articles = this.querySelectorAll(".blog__article");
      this.pagination = this.querySelector("[data-blog-pagination]");
      this.paginationNext = this.querySelector("[data-pagination-next]");
      this.handlePopstate = this.handlePopstate.bind(this);
      this.handlePaginationClick = this.handlePaginationClick.bind(this);

      if (this.select) {
        this.select.addEventListener("change", this.handleChange.bind(this));
      }

      this.pagination?.addEventListener("click", this.handlePaginationClick);
      window.addEventListener("popstate", this.handlePopstate);
    }

    connectedCallback() {
      this.applyState({ syncUrl: true });
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      window.removeEventListener("popstate", this.handlePopstate);
    }

    get params() {
      return new URLSearchParams(window.location.search);
    }

    get category() {
      return this.params.get("category") || "";
    }

    get currentPage() {
      const page = Number(this.params.get("page"));

      return page > 0 ? page : 1;
    }

    get itemsPerPage() {
      return this.config.itemsPerPage || 12;
    }

    handleChange() {
      this.updateHistory({ category: this.select.value, page: 1 });
      this.applyState();
    }

    handlePopstate() {
      this.applyState();
    }

    handlePaginationClick(event) {
      const link = event.target.closest("[data-page]");

      if (!link || link.getAttribute("aria-disabled") === "true") {
        return;
      }

      event.preventDefault();
      this.updateHistory({ page: Number(link.dataset.page) });
      this.applyState();
    }

    getMatchingArticles() {
      const matching = [];

      for (const article of this.articles) {
        if (this.matchesCategory(article)) {
          matching.push(article);
        }
      }

      return matching;
    }

    matchesCategory(element) {
      const { category } = this;

      if (!category) {
        return true;
      }

      return (
        (element.dataset.category || element.value) === category ||
        element.dataset.categoryHandle === category
      );
    }

    applyState({ syncUrl = false } = {}) {
      const matching = this.getMatchingArticles();
      const pageCount = Math.max(
        1,
        Math.ceil(matching.length / this.itemsPerPage),
      );
      const page = Math.min(this.currentPage, pageCount);
      const start = (page - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;

      for (const article of this.articles) {
        article.hidden = true;
      }

      for (const [index, article] of matching.entries()) {
        article.hidden = index < start || index >= end;
      }

      this.updateCount(matching.length);
      this.updatePagination(page, pageCount);

      const selectedCategory = this.updateSelect();

      if (
        syncUrl &&
        (page !== this.currentPage || selectedCategory !== this.category)
      ) {
        this.updateHistory({ category: selectedCategory, page, replace: true });
      }
    }

    updateSelect() {
      if (!this.select) {
        return this.category;
      }

      const { category } = this;

      if (!category) {
        this.select.value = "";
        return "";
      }

      for (const option of this.select.options) {
        if (this.matchesCategory(option)) {
          this.select.value = option.value;
          return option.value;
        }
      }

      this.select.value = "";
      return "";
    }

    updateCount(count) {
      if (!this.count) {
        return;
      }

      const template = this.config.showingPosts || "";
      this.count.textContent = template.replace("__COUNT__", String(count));
    }

    updateHistory({
      category = this.category,
      page = this.currentPage,
      replace = false,
    } = {}) {
      const method = replace ? "replaceState" : "pushState";
      window.history[method]({}, "", this.buildUrl({ category, page }));
    }

    buildUrl({ category = this.category, page = 1 } = {}) {
      const url = new URL(window.location.href);

      if (category) {
        url.searchParams.set("category", category);
      } else {
        url.searchParams.delete("category");
      }

      if (page > 1) {
        url.searchParams.set("page", String(page));
      } else {
        url.searchParams.delete("page");
      }

      return `${url.pathname}${url.search}`;
    }

    updatePagination(currentPage, pageCount) {
      if (!this.pagination) {
        return;
      }

      if (pageCount <= 1) {
        this.pagination.hidden = true;
        return;
      }

      this.pagination.hidden = false;
      this.syncNavButton(
        this.pagination.querySelector("[data-pagination-prev]"),
        currentPage > 1 ? currentPage - 1 : null,
      );
      this.syncNavButton(
        this.paginationNext,
        currentPage < pageCount ? currentPage + 1 : null,
      );

      const pageItems = this.pagination.querySelectorAll(
        ".pagination__item-page",
      );

      for (const item of pageItems) {
        item.remove();
      }

      const parts = this.getPageParts(currentPage, pageCount);

      for (const part of parts) {
        this.paginationNext
          ?.closest("li")
          ?.before(this.createPageItem(part, currentPage));
      }
    }

    syncNavButton(button, page) {
      if (!button) {
        return;
      }

      const item = button.closest("li");

      if (page) {
        button.href = this.buildUrl({ page });
        button.dataset.page = String(page);
        button.classList.remove("pagination__button--disabled");
        button.removeAttribute("aria-disabled");
        item?.removeAttribute("hidden");
        return;
      }

      button.removeAttribute("href");
      button.removeAttribute("data-page");
      button.classList.add("pagination__button--disabled");
      button.setAttribute("aria-disabled", "true");
      item?.setAttribute("hidden", "");
    }

    getPageParts(currentPage, pageCount) {
      const windowSize = 2;
      const pages = new Set([1, pageCount]);

      for (
        let index = currentPage - windowSize;
        index <= currentPage + windowSize;
        index++
      ) {
        if (index >= 1 && index <= pageCount) {
          pages.add(index);
        }
      }

      const sorted = [...pages].sort((first, second) => first - second);
      const parts = [];
      let previous = 0;

      for (const page of sorted) {
        if (previous && page - previous > 1) {
          parts.push("ellipsis");
        }

        parts.push(page);
        previous = page;
      }

      return parts;
    }

    createPageItem(part, currentPage) {
      const item = document.createElement("li");
      item.className = "pagination__item-page";

      if (part === "ellipsis") {
        const ellipsis = document.createElement("span");
        ellipsis.className = "pagination__ellipsis";
        ellipsis.setAttribute("aria-hidden", "true");
        ellipsis.textContent = "…";
        item.append(ellipsis);
        return item;
      }

      const label = (this.config.pageLabel || "Page __NUMBER__").replace(
        "__NUMBER__",
        String(part),
      );

      if (part === currentPage) {
        const current = document.createElement("span");
        current.className = "pagination__page pagination__page--current";
        current.setAttribute("aria-current", "page");
        current.setAttribute("aria-label", label);
        current.textContent = String(part);
        item.append(current);
        return item;
      }

      const link = document.createElement("a");
      link.className = "pagination__page";
      link.href = this.buildUrl({ page: part });
      link.dataset.page = String(part);
      link.setAttribute("aria-label", label);
      link.textContent = String(part);
      item.append(link);

      return item;
    }
  },
);
